/**
 * Flow Rating Form Component
 * Form for rating user flows and task completion
 */

import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Feedback, FlowRatingFeedback } from '../../../types/feedback';

interface FlowRatingFormProps {
  data: Partial<Feedback>;
  onChange: (data: Partial<FlowRatingFeedback>) => void;
  validation: { isValid: boolean; errors: Record<string, string> };
  currentStep: number;
  totalSteps: number;
}

const FlowRatingForm: React.FC<FlowRatingFormProps> = ({
  data,
  onChange,
  validation,
  currentStep
}) => {
  const flowData = data as Partial<FlowRatingFeedback>;
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  const completionStatuses = [
    {
      key: 'completed',
      label: 'Completed Successfully',
      description: 'I was able to complete what I came here to do',
      color: 'text-sage-600',
      bgColor: 'bg-sage-50 border-sage-300'
    },
    {
      key: 'abandoned',
      label: 'Abandoned Task',
      description: 'I gave up and couldn\'t complete my task',
      color: 'text-coral-600',
      bgColor: 'bg-coral-50 border-coral-300'
    },
    {
      key: 'helped-needed',
      label: 'Needed Help',
      description: 'I completed it but needed additional help or guidance',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 border-amber-300'
    }
  ];

  const ratingLabels = [
    'Very Difficult',
    'Difficult', 
    'Neutral',
    'Easy',
    'Very Easy'
  ];

  // Detect current flow based on URL path
  const detectFlowName = (): string => {
    const path = window.location.pathname;
    if (path.includes('dashboard')) return 'Crisis Dashboard Navigation';
    if (path.includes('crisis')) return 'Crisis Detail Analysis';
    if (path.includes('map')) return 'Map Exploration';
    if (path.includes('audit')) return 'Audit Trail Review';
    if (path.includes('field')) return 'Field Response';
    if (path.includes('data')) return 'Data Monitoring';
    return 'General Platform Usage';
  };

  React.useEffect(() => {
    onChange({
      ...flowData,
      flowName: detectFlowName(),
      type: 'flow-rating'
    });
  }, []);

  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-serif text-slate-800 mb-2">
            Rate Your Experience
          </h3>
          <p className="text-sm text-slate-600">
            How was your experience with: <span className="font-medium">{flowData.flowName}</span>
          </p>
        </div>

        {/* Completion Status */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-700">
            Task Status
          </label>
          <div className="space-y-2">
            {completionStatuses.map((status) => (
              <button
                key={status.key}
                onClick={() => onChange({ ...flowData, completionStatus: status.key as FlowRatingFeedback['completionStatus'] })}
                className={cn(
                  'w-full p-4 border-2 rounded-lg text-left transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
                  flowData.completionStatus === status.key
                    ? status.bgColor + ' border-current'
                    : 'border-stone-200 hover:border-stone-300'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn('w-4 h-4 rounded-full mt-0.5',
                    status.key === 'completed' && 'bg-sage-400',
                    status.key === 'abandoned' && 'bg-coral-400',
                    status.key === 'helped-needed' && 'bg-amber-400'
                  )} />
                  <div>
                    <div className={cn('font-medium mb-1', status.color)}>
                      {status.label}
                    </div>
                    <p className="text-sm text-slate-600 leading-snug">
                      {status.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Rating Scale */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-slate-700">
            Overall Difficulty Rating
          </label>
          <div className="flex justify-center">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => onChange({ ...flowData, rating: rating as FlowRatingFeedback['rating'] })}
                  onMouseEnter={() => setHoveredRating(rating)}
                  onMouseLeave={() => setHoveredRating(null)}
                  className={cn(
                    'w-12 h-12 rounded-full border-2 transition-all duration-200 flex items-center justify-center',
                    'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
                    (flowData.rating && rating <= flowData.rating) || (hoveredRating && rating <= hoveredRating)
                      ? 'bg-terracotta border-terracotta text-white'
                      : 'border-stone-300 text-stone-400 hover:border-terracotta hover:text-terracotta'
                  )}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-slate-600">
              {hoveredRating 
                ? ratingLabels[hoveredRating - 1]
                : flowData.rating 
                ? ratingLabels[flowData.rating - 1]
                : 'Click to rate from 1 (very difficult) to 5 (very easy)'
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-serif text-slate-800 mb-2">
          Additional Details
        </h3>
        <p className="text-sm text-slate-600">
          Help us understand what could be improved
        </p>
      </div>

      <div className="space-y-4">
        {/* Difficult Steps */}
        {(flowData.completionStatus === 'abandoned' || flowData.completionStatus === 'helped-needed') && (
          <div>
            <label htmlFor="difficult-steps" className="block text-sm font-medium text-slate-700 mb-2">
              What steps were most challenging?
            </label>
            <textarea
              id="difficult-steps"
              rows={3}
              value={flowData.difficultSteps?.join('\n') || ''}
              onChange={(e) => onChange({ 
                ...flowData, 
                difficultSteps: e.target.value.split('\n').filter(step => step.trim())
              })}
              placeholder="Describe the specific steps or parts that were difficult..."
              className="input-minimal resize-none"
            />
          </div>
        )}

        {/* Suggestions */}
        <div>
          <label htmlFor="suggestions" className="block text-sm font-medium text-slate-700 mb-2">
            Suggestions for Improvement (Optional)
          </label>
          <textarea
            id="suggestions"
            rows={3}
            value={flowData.suggestions || ''}
            onChange={(e) => onChange({ ...flowData, suggestions: e.target.value })}
            placeholder="What would make this experience better? Any specific suggestions?"
            className="input-minimal resize-none"
          />
          <p className="text-xs text-slate-500 mt-1">
            Your suggestions help us prioritize improvements to humanitarian data tools
          </p>
        </div>

        {/* Summary */}
        <div className="bg-stone-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-slate-800 mb-3">Your Rating Summary</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Task:</span>
              <span className="text-sm text-slate-600">{flowData.flowName}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <span className="text-sm text-slate-600">
                {completionStatuses.find(s => s.key === flowData.completionStatus)?.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Difficulty:</span>
              <div className="flex items-center gap-1">
                {flowData.rating && (
                  <>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <svg
                          key={rating}
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          className={cn(
                            rating <= flowData.rating!
                              ? 'text-terracotta fill-current'
                              : 'text-stone-300'
                          )}
                        >
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="currentColor" />
                        </svg>
                      ))}
                    </div>
                    <span className="text-sm text-slate-600 ml-2">
                      ({ratingLabels[flowData.rating - 1]})
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowRatingForm;